// View model for the tracer UI.
//
// This keeps the SHAPE the v1 templates were written against (PascalCase
// Request/Trace, a Session with `requests`), so the UI ports across intact.
// What changed underneath is where those objects come from and what a Trace
// means:
//
//   v1: Request.Traces[] held BOTH the inbound leg and the airline legs,
//       discriminated by a `Source` string ('json-api' | 'sidecar' |
//       'requester'). Deciding what was inbound meant inspecting that string —
//       which is why airline sub-requests sometimes rendered as separate
//       top-level rows.
//
//   v2: the API returns the nesting explicitly. An inbound request has its
//       airline calls nested underneath it. The adapter in tracer.service.ts
//       maps that onto these types, synthesising `Source` so the existing
//       source-filtering pipes keep working.
//
// Keeping Source as a synthetic field is deliberate: it lets the v1 templates
// and pipes work unchanged while the data underneath is correctly nested.

/** Server-computed status. Never derived client-side any more. */
export type Status = 'ok' | 'warning' | 'error' | 'not_set';
export type RequestAggregateStatus = Status;

/** Session-level status, aggregated server-side across requests AND legs. */
export type SessionStatus = 'clean' | 'with_warnings' | 'with_errors' | 'pending';

/** Why an outbound leg failed. Empty when it did not. */
export type ErrorKind = '' | 'timeout' | 'transport' | 'parse' | 'http';

// Trace sources. In v2 these are synthesised by the adapter rather than stored:
// the inbound leg becomes 'json-api', each airline call becomes 'requester'.
export const INBOUND_TRACE_SOURCES = ['json-api', 'sidecar'] as const;
export type InboundTraceSource = (typeof INBOUND_TRACE_SOURCES)[number];
export type FilterableTraceSource = InboundTraceSource | 'requester';

export const isInboundTraceSource = (source: string | undefined): boolean =>
  source === 'json-api' || source === 'sidecar';

/**
 * A fetchable body. Bodies are never inlined in list or tree responses — the
 * API returns a URL, which is what keeps responses small regardless of how
 * large an AirShopping payload is.
 */
export interface BodyLink {
  url: string;
  bytes: number;
}

export interface Message {
  CreatedAt: string;
  Format: string;
  ID: number;
  Name: string;
  TraceID: number;
  /** v2: where to fetch the body from. Resolved lazily by the detail view. */
  BodyUrl?: string;
  Bytes?: number;
  /**
   * v2: the message's HTTP headers, masked by the tracer (Authorization and
   * other secrets keep a few characters only). An empty value means the
   * header was sent empty. Undefined when none were recorded.
   */
  Headers?: Record<string, string>;
  /**
   * v2: whether the tracer records headers for this kind of message (the
   * partner's request, and the call to the airline and its response). Tells
   * "not recorded" (older traces) apart from "not kept for this message".
   */
  HeadersKept?: boolean;
}

export interface Trace {
  ID: number;
  RequestID: number;
  Providers: string;
  Status: string;
  /** Synthetic in v2 — see the file header. */
  Source: string;
  Label?: string;
  Messages: Message[];
  CreatedAt: string;
  Order?: number;

  // v2 additions, available on airline legs.
  /** NDC method of THIS call. 'Login' for provider login sub-calls. */
  ProviderMethod?: string;
  /** The endpoint actually hit. Login legs carry the provider's auth URL. */
  Url?: string;
  HttpMethod?: string;
  DurationMs?: number;
  HttpStatus?: number;
  ErrorKind?: ErrorKind;
}

export interface Request {
  ID: number;
  Application: string;
  Consumer: string;
  RequestID: string;
  SessionID: number;
  Method: string;
  ProviderMethod: string;
  Agent: string;
  Agency: string;
  IsCollapsed: boolean;
  Traces: Trace[];
  CreatedAt: string;
  Provider?: string;
  VersionID?: string;
  Status: string;
  AggregateStatus?: RequestAggregateStatus;

  // v2 additions.
  DurationMs?: number;
  HttpStatus?: number;
}

export interface Session {
  id: number;
  value: string;
  created_at: string;
  updated_at: string;
  requests: Request[];
  important: boolean;
  status: SessionStatus;

  // v2 list columns, used by the home table without walking `requests`.
  request_count?: number;
  error_count?: number;
  warning_count?: number;
  applications?: string[];
  consumers?: string[];
  agencies?: string[];
  methods?: string[];
  providers?: string[];
}

export interface TracerResponse {
  sessions: Session[];
  /** Keyset cursor for the next page. Absent on the last page. */
  next_cursor?: string;
  error?: string;
}

export interface SessionResponse {
  session?: Session;
  requests?: Request[];
  providers?: string[];
  error?: string;
  /** Set when the search matched a request id rather than a session id. */
  matchedRequestId?: string;
}

export interface MessageResponse {
  body: string;
  error?: string;
}

export interface StatRow {
  key: string;
  calls: number;
  errors: number;
  error_pct: number;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
}

export interface StatsResponse {
  dimension: StatDimension;
  from: string;
  to: string;
  rows: StatRow[];
}

export type StatDimension = 'provider' | 'endpoint' | 'method' | 'consumer';

/** Filters accepted by the sessions list. All optional and combinable. */
export interface SessionFilters {
  from?: string;
  to?: string;
  application?: string;
  consumer?: string;
  agency?: string;
  agent?: string;
  method?: string;
  provider?: string;
  status?: SessionStatus;
  anonymous?: boolean;
  cursor?: string;
  limit?: number;
}

/**
 * True when a leg is a provider login sub-call.
 *
 * Worth surfacing: a login leg appears only on a cache MISS, because the
 * requester short-circuits on a cached token. Two otherwise identical Vueling
 * sessions can therefore show different leg counts, which reads as missing data
 * unless it is labelled.
 */
export const isLoginLeg = (t: Trace): boolean => t.ProviderMethod === 'Login';

/** Methods whose partial fan-out failure is not a warning. */
export const isShoppingMethod = (method: string): boolean => {
  const m = method?.toLowerCase();
  return m === 'airshopping' || m === 'itinshopping';
};
